import {
  Component,
  input,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  BarChartData,
  BarChartColorName,
  BarChartColorLevel,
  BarChartShape,
} from '@interfaces';
import { resolveCssCustomProperty } from '@helpers';

@Component({
  selector: 'fip-vertical-bar-chart',
  templateUrl: './vertical-bar-chart.component.html',
  styleUrls: ['./vertical-bar-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VerticalBarChartComponent {
  readonly data = input.required<BarChartData[]>();
  readonly width = input<number>(800);
  readonly height = input<number>(400);
  readonly static = input<boolean>(true);
  readonly colorName = input<BarChartColorName>('green');
  readonly colorLevel = input<BarChartColorLevel>('500');
  readonly timeline = input<boolean>(true);
  readonly rangeMin = input<number>();
  readonly rangeMax = input<number>();
  readonly margins = input<{ l: number; r: number; t: number; b: number }>({
    l: 56,
    r: 56,
    t: 16,
    b: 64,
  });
  readonly xTicksuffix = input<string>('');
  readonly yTicksuffix = input<string>('');
  readonly shapes = input<BarChartShape[]>([]);

  /**
   * Computes the Plotly data configuration for the bar chart.
   *
   * Transforms the input data into Plotly-compatible format, applying:
   * - Timeline formatting for x-axis values (converts to localized date strings if timeline is enabled)
   * - Color theming using CSS custom properties based on colorName and colorLevel inputs
   * - Bar chart type configuration
   *
   * @returns Array of Plotly data objects with x/y coordinates, chart type, and styling
   */
  readonly plotlyData = computed<unknown[]>(() => {
    const data = this.data();
    const color = resolveCssCustomProperty(
      `--tw-color-${this.colorName()}-${this.colorLevel()}`,
    );

    return (
      data?.map((dataset) => ({
        x: this.timeline()
          ? dataset.x.map((value) => new Date(value).toLocaleDateString())
          : dataset.x,
        y: dataset.y,
        type: 'bar',
        marker: {
          color: color || 'black',
        },
      })) ?? []
    );
  });

  /**
   * Computes the Plotly layout configuration for the bar chart.
   *
   * Configures chart dimensions, margins, axis settings, and overlay shapes:
   * - Sets chart width/height from component inputs
   * - Applies custom margins with auto-expand disabled and padding
   * - Configures y-axis range (if specified) and tick suffix
   * - Configures x-axis tick suffix
   * - Renders overlay shapes with dynamic color theming and positioning
   *
   * @returns Plotly layout object with dimensions, margins, axes, and shape configurations
   */
  readonly plotlyLayout = computed(() => {
    const margins = this.margins();
    const shapes = this.shapes();

    return {
      width: this.width(),
      height: this.height(),
      margin: {
        l: margins.l,
        r: margins.r,
        t: margins.t,
        b: margins.b,
        autoexpand: false,
        pad: 16,
      },
      yaxis: {
        range: [this.rangeMin(), this.rangeMax()],
        ticksuffix: this.yTicksuffix(),
      },
      xaxis: {
        ticksuffix: this.xTicksuffix(),
      },
      shapes: shapes.map((shape) => {
        const lineColor = resolveCssCustomProperty(
          `--tw-color-${shape.line.colorName}-${shape.line.colorLevel}`,
        );

        const labelColor = resolveCssCustomProperty(
          `--tw-color-${shape.label.colorName}-${shape.label.colorLevel}`,
        );

        return {
          type: 'line',
          xref: 'paper',
          x0: shape.data.x0,
          x1: shape.data.x1,
          y0: shape.data.y0,
          y1: shape.data.y1,
          line: {
            color: lineColor,
            width: shape.line.width,
            dash: shape.line.dash,
          },
          label: {
            text: shape.label.text,
            font: { size: 12, color: labelColor },
            textposition: shape.label.position,
            padding: 8,
          },
        };
      }),
    };
  });

  /**
   * Computes the Plotly configuration options for chart interactivity.
   *
   * Controls chart interaction behavior based on the static input:
   * - When static is true, disables all user interactions (zoom, pan, hover, etc.)
   * - When static is false, enables full chart interactivity
   *
   * @returns Plotly configuration object with staticPlot setting
   */
  readonly plotlyConfig = computed(() => ({
    staticPlot: this.static(),
  }));
}
